import { useId, useState } from "react";
import { FiPlus, FiTrash2, FiX } from "react-icons/fi";
import {
  POLL_MAX_DURATION_MINUTES,
  POLL_MAX_OPTIONS,
  POLL_MIN_DURATION_MINUTES,
  type PollEditorValidation,
  type PollEditorValue,
} from "@/features/poll/lib/pollEditor";
import { Button, Input } from "@/shared/ui/form";

type PollEditorProps = {
  value: PollEditorValue;
  validation: PollEditorValidation;
  onChange: (value: PollEditorValue) => void;
  isDisabled?: boolean;
  showAllErrors?: boolean;
};

export function PollEditor({
  value,
  validation,
  onChange,
  isDisabled = false,
  showAllErrors = false,
}: PollEditorProps): React.ReactNode {
  const idPrefix = useId();
  const [touchedOptions, setTouchedOptions] = useState<Set<number>>(new Set());
  const [isDurationTouched, setIsDurationTouched] = useState(false);

  function setEnabled(isEnabled: boolean): void {
    setTouchedOptions(new Set());
    setIsDurationTouched(false);
    onChange({ ...value, isEnabled });
  }

  if (!value.isEnabled) {
    return (
      <Button
        type="button"
        variant="secondary"
        icon={<FiPlus aria-hidden="true" />}
        disabled={isDisabled}
        onClick={(): void => setEnabled(true)}
      >
        アンケートを追加
      </Button>
    );
  }

  return (
    <fieldset className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
      <legend className="px-1 text-sm font-medium text-foreground">
        アンケート
      </legend>
      <p className="text-xs text-muted-foreground">
        投稿本文を質問として表示します。選択肢は2〜5件です。
      </p>
      <ol className="space-y-2">
        {value.options.map((option, index) => {
          const inputId = `${idPrefix}-option-${index}`;
          const errorId = `${inputId}-error`;
          const error = validation.optionErrors[index] ?? null;
          const showError =
            Boolean(error) && (showAllErrors || touchedOptions.has(index));
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: options 0-1 are fixed and later entries are only removed by their own index
            <li key={index} className="space-y-1">
              <label
                htmlFor={inputId}
                className="block text-sm text-foreground"
              >
                選択肢 {index + 1}
              </label>
              <span className="flex items-center gap-2">
                <Input
                  id={inputId}
                  type="text"
                  value={option}
                  disabled={isDisabled}
                  error={showError}
                  aria-invalid={showError}
                  aria-describedby={showError ? errorId : undefined}
                  className="min-w-0 flex-1"
                  onChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                  ): void => {
                    const options = [...value.options];
                    options[index] = event.target.value;
                    onChange({ ...value, options });
                  }}
                  onBlur={(): void =>
                    setTouchedOptions((current) => new Set(current).add(index))
                  }
                />
                {index >= 2 && (
                  <button
                    type="button"
                    disabled={isDisabled}
                    className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-border text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={(): void => {
                      setTouchedOptions(
                        (current) =>
                          new Set(
                            [...current].flatMap((touchedIndex) => {
                              if (touchedIndex < index) return [touchedIndex];
                              if (touchedIndex > index)
                                return [touchedIndex - 1];
                              return [];
                            }),
                          ),
                      );
                      onChange({
                        ...value,
                        options: value.options.filter(
                          (_entry, optionIndex) => optionIndex !== index,
                        ),
                      });
                    }}
                  >
                    <FiTrash2 aria-hidden="true" />
                    <span className="sr-only">選択肢 {index + 1}を削除</span>
                  </button>
                )}
              </span>
              {showError && (
                <p
                  id={errorId}
                  role="alert"
                  className="text-xs text-destructive"
                >
                  {error}
                </p>
              )}
            </li>
          );
        })}
      </ol>
      {value.options.length < POLL_MAX_OPTIONS && (
        <Button
          type="button"
          variant="secondary"
          icon={<FiPlus aria-hidden="true" />}
          disabled={isDisabled}
          onClick={(): void =>
            onChange({ ...value, options: [...value.options, ""] })
          }
        >
          選択肢を追加
        </Button>
      )}
      <p className="space-y-1">
        <label
          htmlFor={`${idPrefix}-duration`}
          className="block text-sm text-foreground"
        >
          アンケート受付期間（分）
        </label>
        <Input
          id={`${idPrefix}-duration`}
          type="number"
          inputMode="numeric"
          min={POLL_MIN_DURATION_MINUTES}
          max={POLL_MAX_DURATION_MINUTES}
          step={1}
          value={value.durationMinutes}
          disabled={isDisabled}
          error={
            Boolean(validation.durationError) &&
            (showAllErrors || isDurationTouched)
          }
          aria-invalid={
            Boolean(validation.durationError) &&
            (showAllErrors || isDurationTouched)
          }
          aria-describedby={`${idPrefix}-duration-hint${validation.durationError && (showAllErrors || isDurationTouched) ? ` ${idPrefix}-duration-error` : ""}`}
          className="w-28"
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            onChange({ ...value, durationMinutes: event.target.value })
          }
          onBlur={(): void => setIsDurationTouched(true)}
        />
        <small
          id={`${idPrefix}-duration-hint`}
          className="block text-xs text-muted-foreground"
        >
          {POLL_MIN_DURATION_MINUTES}〜{POLL_MAX_DURATION_MINUTES}分
        </small>
        {validation.durationError && (showAllErrors || isDurationTouched) && (
          <small
            id={`${idPrefix}-duration-error`}
            role="alert"
            className="block text-xs text-destructive"
          >
            {validation.durationError}
          </small>
        )}
      </p>
      <Button
        type="button"
        variant="ghost"
        icon={<FiX aria-hidden="true" />}
        disabled={isDisabled}
        onClick={(): void => setEnabled(false)}
      >
        アンケートを取り消す
      </Button>
    </fieldset>
  );
}
